import React, {
    createContext,
    ReactElement,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { Alert, Snackbar } from '@mui/material'

export type SnackType = 'success' | 'info' | 'warning' | 'error'

export interface SnackInfo {
    alert(value: string, duration?: number): void
    info(value: string, duration?: number): void
    success(value: string, duration?: number): void
    error(value: string, duration?: number): void
    connectionFail(err: unknown, duration?: number): void
}

const SnackInfoDefault: SnackInfo = {
    alert: () => {},
    info: () => {},
    success: () => {},
    error: () => {},
    connectionFail: () => {},
}

interface Props {
    children: React.ReactNode
}

const SnackContext = createContext<SnackInfo>(SnackInfoDefault)

export function useSnack() {
    return useContext(SnackContext)
}

export function SnackProvider(props: Props): ReactElement {
    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState('')
    const [type, setType] = useState<SnackType>('info')
    const [duration, setDuration] = useState(2000)

    const alert = useCallback((value: string, duration?: number) => {
        setMessage(value)
        setType('warning')
        duration && setDuration(duration)
        setOpen(true)
    }, [])

    const success = useCallback((value: string, duration?: number) => {
        setMessage(value)
        setType('success')
        duration && setDuration(duration)
        setOpen(true)
    }, [])

    const info = useCallback((value: string, duration?: number) => {
        setMessage(value)
        setType('info')
        duration && setDuration(duration)
        setOpen(true)
    }, [])

    const error = useCallback((value: string, duration?: number) => {
        setMessage(value)
        setType('error')
        duration && setDuration(duration)
        setOpen(true)
    }, [])

    const connectionFail = useCallback((err: unknown, duration = 6000) => {
        let message =
            'Falha na comunicação. Verifique sua conexão e tente novamente.'
        if (err instanceof Error) {
            const detail = (err as any).detail
            message = detail
                ? `Error servidor: ${err.message}\n${detail}`
                : `Error servidor: ${err.message}`
        }

        setMessage(message)
        setType('error')
        duration && setDuration(duration)
        setOpen(true)
    }, [])

    const value = useMemo(
        () => ({ alert, success, info, error, connectionFail }),
        [alert, success, info, error, connectionFail]
    )

    return (
        <SnackContext.Provider value={value}>
            {props.children}
            <Snackbar
                open={open}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={duration}
                onClose={() => setOpen(false)}
            >
                <Alert variant="filled" severity={type}>
                    {message}
                </Alert>
            </Snackbar>
        </SnackContext.Provider>
    )
}

export default SnackContext
